import {Component, Inject} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, ValidationErrors, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { EDGE_STYLE_ROUTING, EMAIL_MAX, LAYOUT_DIRECTION, NODE_MOVE_OPTION, REQUESTABLE_ROLES, USER_NAME_MAX, USER_NAME_MIN, USER_ROLE, User, createUserPreferences, getRoleDescription, getUserRoleName, isValidEmail } from '../common/user';
import { FREE_USERS, getCurrency } from '../common/organisation';
import { OrganisationService } from '../organisation/organisation.service';
import { UserService } from './user.service';

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = form && form.submitted;
      return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

export interface UserDialogData {
    user:User
    isInvite:boolean
    numExistingUsers?:number    // for an invite, to explain any change to the bill
    exempt?:boolean             // the organisation isn't billed
}

// edits a user's details and preferences, or invites someone to the organisation.
// Closes with an updated copy; the caller saves it, so cancelling or a failed save leaves the user unchanged
@Component({
    selector: 'user-dialog',
    templateUrl: 'user-dialog.html',
    styleUrls: ['user-dialog.css'],
    standalone: false
})
export class UserDialogComponent {
    user:User
    userForm: FormGroup
    roleControl:FormControl<USER_ROLE>|undefined
    isInvite:boolean
    message:string

    matcher = new MyErrorStateMatcher();
    LAYOUT_DIRECTION = LAYOUT_DIRECTION
    NODE_MOVE_OPTION = NODE_MOVE_OPTION
    EDGE_STYLE_ROUTING = EDGE_STYLE_ROUTING
    USER_NAME_MAX = USER_NAME_MAX
    EMAIL_MAX = EMAIL_MAX
    roles = REQUESTABLE_ROLES.map(role => ({role, name:getUserRoleName(role), description:getRoleDescription(role)}))

    isSelf:boolean

    constructor(public dialogRef: MatDialogRef<UserDialogComponent, User>, private formBuilder: FormBuilder, private organisationService:OrganisationService,
        userService:UserService, @Inject(MAT_DIALOG_DATA) public userData:UserDialogData) {

        this.user = userData.user || {} as User
        this.isInvite = userData.isInvite
        this.isSelf = !this.isInvite && this.user._key === userService.getUser()?._key
        const preferences = this.user.preferences || createUserPreferences()

        if (this.isInvite) {
            this.roleControl = new FormControl<USER_ROLE>(USER_ROLE.READ, {nonNullable:true, validators:Validators.required})
            this.message = this.getBillingMessage()
        }

        this.userForm = this.formBuilder.group({
            name: [this.user.name, [Validators.required, Validators.minLength(USER_NAME_MIN), Validators.maxLength(USER_NAME_MAX), this.trimmedLength]],
            email: this.isInvite ? [this.user.email, [Validators.required, this.validEmail, this.notMember]] : [{value:this.user.email, disabled:true}],
            graphDirection: [preferences.graphDirection, [Validators.required]],
            nodeMoveOption: [preferences.nodeModeOption, [Validators.required]],
            edgeRoutingStyle: [preferences.edgeRoutingStyle, [Validators.required]]
        })
    }

    get title() {
        if (this.isInvite) return 'Invite someone to the organisation'
        return this.isSelf ? 'Your details' : `Details for ${this.user.name || this.user.email}`
    }

    // what inviting another person does to the bill
    private getBillingMessage() {
        if (this.userData.exempt || this.userData.numExistingUsers === undefined) return ''
        const currency = getCurrency(this.organisationService.organisation?.currencyCode)
        if (this.userData.numExistingUsers === FREE_USERS)
            return `This takes you over the free limit of ${FREE_USERS} ${FREE_USERS === 1 ? 'user' : 'users'}. Each user above the limit at the end of the month is billed ${currency.userPrice}. Cancel if you want to stay on the free plan.`
        if (this.userData.numExistingUsers > FREE_USERS)
            return `Each extra user adds ${currency.userPrice} to your monthly bill.`
        return ''
    }

    // a name of spaces would pass the length checks
    private trimmedLength = (control:AbstractControl):ValidationErrors | null => {
        const length = (control.value || '').trim().length
        return control.value && length < USER_NAME_MIN ? {minlength:true} : null
    }

    private validEmail = (control:AbstractControl):ValidationErrors | null => {
        return !control.value || isValidEmail(control.value.trim()) ? null : {email:true}
    }

    // the person this email belongs to, if they're already in the organisation
    private findMember(email:string) {
        const clean = (email || '').trim().toLowerCase()
        // the organisation's user list says whether each person has accepted their invitation
        const user = clean && this.organisationService.users?.find(u => (u.email || '').toLowerCase() === clean)
        return user ? {user, activated:!!(user as User & {activated?:boolean}).activated} : undefined
    }

    private notMember = (control:AbstractControl):ValidationErrors | null => {
        return this.findMember(control.value)?.activated ? {member:true} : null
    }

    // someone invited who hasn't accepted yet is sent the invitation again
    isPendingInvite() {
        const member = this.isInvite && this.findMember(this.userForm.controls['email'].value)
        return !!member && !member.activated
    }

    getMemberName() {
        return this.findMember(this.userForm.controls['email'].value)?.user.name
    }

    save() {
        this.userForm.markAllAsTouched()
        this.roleControl?.markAsTouched()
        if (this.userForm.invalid || this.roleControl?.invalid) return

        const values = this.userForm.getRawValue()
        const result:User = {
            ...this.user,
            name: values.name.trim(),
            email: this.isInvite ? values.email.trim() : this.user.email,
            preferences: {
                ...(this.user.preferences || createUserPreferences()),
                graphDirection: values.graphDirection,
                nodeModeOption: values.nodeMoveOption,
                edgeRoutingStyle: values.edgeRoutingStyle,
            },
        }
        if (this.roleControl) result.role = this.roleControl.value

        this.dialogRef.close(result);
    }

    getNameError() {
        return `Name must be between ${USER_NAME_MIN} and ${USER_NAME_MAX} characters`
    }
}
